import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const KEY_TO_STEP = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * Folder-style tab strip plus the panel it opens into. The selected tab is
 * white and joins the outlined panel below it; idle tabs are tinted.
 *
 * Implements the WAI-ARIA tabs pattern: roving tabindex, Left/Right arrows
 * move between visible tabs (wrapping), Home/End jump to the ends. The
 * selected tab is scrolled into view so a horizontally scrolling strip on
 * narrow screens always shows the open tab.
 *
 * @param props.idPrefix Unique prefix for the tab and panel element ids.
 * @param props.tabs Array of {key, label, icon, hidden}. Hidden tabs stay in
 * the DOM (display: none via CSS) and are skipped by keyboard navigation.
 * @param props.selected Key of the selected tab.
 * @param props.onSelect Called with the key of the tab to select.
 * @param props.panelClassName Extra class for the panel, for page-specific padding.
 * @param props.scrollToId Optional id of the section title above the tabs. When
 * given, every tab selection (click or keyboard) scrolls that element to the
 * top of the viewport so the open panel starts in view.
 * @param props.children Panel content.
 * @return JSX
 */
export function FolderTabs({ idPrefix, tabs, selected, onSelect, panelClassName, scrollToId, children }) {
  const selectedRef = useRef(null);
  const panelId = `${idPrefix}Panel`;
  const tabId = (key) => `${idPrefix}Tab-${key}`;

  // Select a tab and, when the page named its title element, bring that
  // title to the top. Deferred a frame so the newly selected panel has
  // rendered before the browser measures where to scroll.
  const selectTab = (key) => {
    onSelect(key);
    if (scrollToId) {
      requestAnimationFrame(() => {
        document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);

  const handleKeyDown = (event) => {
    const visible = tabs.filter((tab) => !tab.hidden);
    const current = visible.findIndex((tab) => tab.key === selected);
    let next;
    if (event.key in KEY_TO_STEP) {
      next = (current + KEY_TO_STEP[event.key] + visible.length) % visible.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = visible.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    selectTab(visible[next].key);
    document.getElementById(tabId(visible[next].key))?.focus();
  };

  return (
    <div className="folderTabs">
      <div className="folderTabList" role="tablist" onKeyDown={handleKeyDown}>
        {tabs.map(({ key, label, icon, hidden }) => {
          const isSelected = key === selected;
          const className = [
            'folderTab',
            isSelected && 'folderTabSelected',
            hidden && 'folderTabHidden',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={key}
              type="button"
              id={tabId(key)}
              role="tab"
              className={className}
              aria-selected={isSelected}
              aria-controls={panelId}
              tabIndex={isSelected ? 0 : -1}
              ref={isSelected ? selectedRef : null}
              onClick={() => selectTab(key)}
            >
              <FontAwesomeIcon icon={icon} className="folderTabIcon" />&nbsp;{label}
            </button>
          );
        })}
      </div>
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(selected)}
        className={['folderTabPanel', panelClassName].filter(Boolean).join(' ')}
      >
        {children}
      </div>
    </div>
  );
}
